import {
  AuthError,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  fetchAuthSession,
  signUp,
} from 'aws-amplify/auth';

import { myLocalStorage } from '../myLocalStorage';
import { Logger } from '../utils/logger';

type AuthErrorContext = 'initial-check' | 'signin' | 'signout' | 'attempt-auto-signup';

const handleAuthError = (error: unknown, context: AuthErrorContext): AuthError => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const cleanMessage = errorMessage
    .replace('PreSignUp failed with error', '')
    .replace('Error:', '')
    .trim();

  Logger.error(`Auth error (${context}): ${cleanMessage}`);

  return new AuthError({ name: context, message: cleanMessage });
};

export const isSignedIn = async () => {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
};

const attemptAutoSignUp = async (email: string) => {
  const { isSignUpComplete } = await signUp({
    username: email,
    password: 'Ch@ng3?!M3_932183Pl3@s3', // Signing Workflow changes this on the backend.
    options: {
      userAttributes: { email },
      autoSignIn: { enabled: true },
    },
  });

  if (!isSignUpComplete) {
    throw new Error('Sign-up process incomplete');
  }

  const { nextStep } = await amplifySignIn({
    username: email,
    options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
  });
  return nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE';
};

export const signIn = async (email: string): Promise<boolean> => {
  myLocalStorage.setItem('email', email);

  try {
    const { nextStep } = await amplifySignIn({
      username: email,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });
    return nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE';
  } catch (error) {
    handleAuthError(error, 'signin');

    try {
      return await attemptAutoSignUp(email);
    } catch (error) {
      handleAuthError(error, 'attempt-auto-signup');
      return false;
    }
  }
};

export const signOut = async () => {
  try {
    await amplifySignOut();
    ['email', 'person', 'company', 'authToken'].forEach((key) => myLocalStorage.removeItem(key));
  } catch (error) {
    handleAuthError(error, 'signout');
  }
};

export const validateCode = async (code: string) => {
  let success = false;
  let message = '';

  try {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
    if (!isSignedIn) {
      return { success: false, message: 'Email must be set first' };
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        myLocalStorage.setItem('authToken', idToken);
        success = true;
      } else {
        success = false;
        message = 'No token available';
      }
    } catch (error) {
      success = false;
      message = `Failed to get session: ${error instanceof Error ? error.message : String(error)}`;
    }
  } catch (error) {
    success = false;
    message = error instanceof Error ? error.message : String(error);
  }

  return { success, message };
};
